import React, { useContext } from 'react';
import StyledTable from '@shared/components/StyledTable';
import { Checkbox } from '@mui/material';
import CheckedXIcon from '@shared/components/CheckedXIcon';
import { EVENT_APPRAISER_ASSIGNMENT_STATUS } from '../../shared/constants';
import EventAssignmentsTableRoleColumn from './EventAssignmentsTableRoleColumn';
import EventAssignmentsTableRankColumn from './EventAssignmentsTableRankColumn';
import ProfilePopover from './ProfilePopover';
import { AppraisersProfileContext } from '../../shared/contexts/AppraisersProfileContext';
import { userAssignmentService } from '../EditEventPage/shared/userAssignmentService';
import { EVENT_ASSIGNMENTS_TABLE_MODE } from './constants/EventAssignmentsTableMode';

const SCROLL_VERTICAL = 300;

/**
 * @typedef {object} EventAssignmentsTableProps
 * @property {Array<*>} data
 * @property {(externalUserId: number, newValue: number | string | null, field: string) => void} [setATMValue]
 * @property {*} [mode]
 * @property {(teamRole: number) => { takenRankings: Array<number>, isAssignmentFilled?: boolean }} assignmentSettingsProvider
 * @property {string} [rankingColumnName]
 * @property {boolean} [canEditRanking]
 * @property {boolean} [loading]
 * @property {boolean} [showUnavailableAtms]
 * @property {boolean} [showAssignmentRank]
 * @property {boolean} [canAssignATMs]
 */

/**
 * @param {EventAssignmentsTableProps} props
 */
const EventAssignmentsTable = props => {
  const {
    data,
    rankingColumnName = 'Ranking',
    mode,
    assignmentSettingsProvider,
    canEditRanking = false,
    loading = false,
    setATMValue,
    showUnavailableAtms = false,
    showAssignmentRank = false,
    canAssignATMs = false
  } = props;

  const { data: appraisersProfileData, isDataAvailable: isAppraisersProfileDataAvailable } =
    useContext(AppraisersProfileContext);

  const columns = [
    {
      title: 'Role',
      dataIndex: 'currentRoleName',
      width: '25%',
      sorter: (a, b) => a.currentRoleName.localeCompare(b.currentRoleName),
      hidden: mode === EVENT_ASSIGNMENTS_TABLE_MODE.ATL_PREFERENCES
    },
    {
      title: 'Name',
      dataIndex: 'name',
      width: '40%',
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'Profile',
      dataIndex: 'externalUserId',
      width: '25%',
      render: (value, record) => (
        <ProfilePopover
          name={record.name}
          appraiserData={appraisersProfileData.find(
            profileData => profileData.externalUserId === value
          )}
          disabled={isAppraisersProfileDataAvailable}
        />
      )
    },
    {
      title: 'Interest',
      dataIndex: 'interest',
      width: '10rem',
      sorter: (a, b) => a.interest.localeCompare(b.interest)
    },
    {
      title: 'Availability',
      width: '25%',
      dataIndex: 'numOfEventsBeforeAfter',
      render: value => `${value} event(s) around appraisal.`,
      sorter: (a, b) => a.numOfEventsBeforeAfter - b.numOfEventsBeforeAfter
    },
    {
      title: 'ATL Availability',
      width: '10rem',
      dataIndex: 'atlAvailability',
      render: value => (
        <div style={{ textAlign: 'center' }}>
          <Checkbox
            size='small'
            color='default'
            disabled
            checked={value}
            checkedIcon={<CheckedXIcon size='small' />}
          />
        </div>
      ),
      sorter: (a, b) => a.atlAvailability - b.atlAvailability,
      hidden: mode === EVENT_ASSIGNMENTS_TABLE_MODE.TEAM_TABLE
    },
    {
      title: rankingColumnName,
      dataIndex: 'assignmentRank',
      width: '10rem',
      hidden: !showAssignmentRank,
      render: (value, record) => {
        const rowCanEditRanking =
          canEditRanking && record.currentUserAssignmentPermissions.canEditRank;
        return (
          <EventAssignmentsTableRankColumn
            rankValue={value}
            setATMValue={setATMValue}
            rowRecord={record}
            takenRankings={assignmentSettingsProvider(record.teamRole).takenRankings}
            disabled={!rowCanEditRanking}
          />
        );
      },

      sorter: (a, b) => {
        const aRank = a.assignmentRank ?? Infinity;
        const bRank = b.assignmentRank ?? Infinity;

        return aRank - bRank;
      }
    },
    {
      title: 'Assignment',
      hidden: !canAssignATMs,
      dataIndex: 'role',
      width: '11rem',
      render: (value, record) => (
        <EventAssignmentsTableRoleColumn
          atmRoleValue={value}
          setATMValue={setATMValue}
          rowRecord={record}
          isAssignmentFilled={assignmentSettingsProvider(record.teamRole).isAssignmentFilled}
          disabled={!record.currentUserAssignmentPermissions.canEditRole}
        />
      ),
      sorter: (a, b) => a.assignmentRank - b.assignmentRank
    }
  ];

  const visibleColumns = columns.filter(item => !item.hidden);

  const mapData = data.map(assignment => ({
    ...assignment,
    interest: mapUserInterest(assignment.status, assignment.eventBidDate)
  }));

  return (
    <div data-testid='event-assignments-table'>
      <StyledTable
        rowKey='externalUserId'
        dataSource={mapData}
        columns={visibleColumns}
        pagination={false}
        showSorterTooltip={false}
        scroll={{ y: SCROLL_VERTICAL }}
        loading={loading}
        rowClassName={record => {
          if (
            showUnavailableAtms &&
            record.isUnavailableAfterDCR &&
            userAssignmentService.isAssigned(record.role)
          ) {
            return 'highlight-row';
          }
          return '';
        }}
      />
    </div>
  );
};

const mapUserInterest = (status, bidDate) => {
  if (
    status === EVENT_APPRAISER_ASSIGNMENT_STATUS.ACCEPTED ||
    status === EVENT_APPRAISER_ASSIGNMENT_STATUS.PENDING_CONFIRMATION
  ) {
    return 'Accepted';
  }

  if (bidDate) {
    return `Bid - ${bidDate}`;
  }

  if (status === EVENT_APPRAISER_ASSIGNMENT_STATUS.REJECTED) {
    return 'Rejected';
  }

  return 'Bid - N/A';
};

export default EventAssignmentsTable;
